import React, { useEffect, useState } from 'react';
import {
	Box,
	Drawer,
	Toolbar,
	Typography,
	List,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Badge,
	Divider,
	Stack,
} from '@mui/material';
import TopNavigationBar from './TopNavigationBar';
import DashboardIcon from '@mui/icons-material/Dashboard';
import BarChartIcon from '@mui/icons-material/BarChart';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PeopleIcon from '@mui/icons-material/People';
import AssignmentIcon from '@mui/icons-material/Assignment';
import RefreshIcon from '@mui/icons-material/Refresh';
import DescriptionIcon from '@mui/icons-material/Description';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SettingsIcon from '@mui/icons-material/Settings';
import { useLocation, useNavigate } from 'react-router-dom';
import { AssignmentInd, MenuOutlined, Shield } from '@mui/icons-material';
import { getElderHomeName } from '../Utils/AuthUtil';
import { dark, teal } from '../assets/theme/Colors';

const drawerWidth = 200;

export default function SideNavigationBar() {
	const navigate = useNavigate();
	const location = useLocation();
	const homeName = getElderHomeName();
	const [tabName, setTabName] = useState(location.pathname);

	useEffect(() => {
		setTabName(location.pathname);
	}, [location.pathname]);

	const menu = [
		{
			label: 'Overview',
			items: [
				{
					key: '/home',
					label: 'Resident 360',
					icon: DashboardIcon,
				},
				{
					key: '/systemUsage',
					label: 'System Usage',
					icon: BarChartIcon,
				},
			],
		},
		{
			label: 'Residents',
			items: [
				{
					key: '/addResident',
					label: 'Onboard',
					icon: PersonAddIcon,
				},
				{
					key: '/profiles',
					label: 'All Profiles',
					icon: PeopleIcon,
				},
				{
					key: '/admissionAssessment',
					label: 'Admission Assessment',
					icon: AssignmentInd,
				},
			],
		},
		{
			label: 'AI Modules',
			items: [
				{
					key: '/initialCarePlan',
					label: 'Care Plans',
					icon: AssignmentIcon,
				},
				{
					key: '/carePlan',
					label: 'Optimise Plan',
					icon: RefreshIcon,
				},
				{
					key: '/summarizer',
					label: 'Summariser',
					icon: DescriptionIcon,
				},
				{
					key: '/notifications',
					label: 'CQC Reports',
					icon: MedicalServicesIcon,
				},
				{
					key: '/configuration',
					label: 'Templates',
					icon: MenuOutlined,
				},
				{
					key: '/planValidator',
					label: 'Plan Validator',
					icon: Shield,
				},
			],
		},
		{
			label: 'Admin',
			items: [
				// {
				// 	key: '/users',
				// 	label: 'User Mgmt',
				// 	icon: AdminPanelSettingsIcon,
				// },
				{
					key: '/settings',
					label: 'Settings',
					icon: SettingsIcon,
				},
			],
		},
	];

	const onClickLogo = () => {
		navigate('/home');
	};

	const getTitle = () => {
		const section = menu.find(section =>
			section.items?.some(item => item.key === tabName),
		);

		const item = section?.items?.find(item => item.key === tabName);

		if (item?.label) {
			return item.label;
		}

		// fallback from tabName
		return tabName
			.split('/')
			.filter(Boolean)[0]
			?.replace(/-/g, ' ')
			.replace(/\b\w/g, char => char.toUpperCase());
	};

	return (
		<Box sx={{ display: 'flex' }}>
			{/* TOPBAR */}
			<TopNavigationBar
				title={getTitle()}
				onBack={() => navigate(-1)}
				onForward={() => navigate(1)}
			/>

			{/* SIDEBAR */}
			<Drawer
				variant="permanent"
				sx={{
					width: drawerWidth,
					flexShrink: 0,
					'& .MuiDrawer-paper': {
						width: drawerWidth,
						bgcolor: dark,
						color: 'rgba(255,255,255,0.75)',
						boxSizing: 'border-box',

						overflowY: 'auto',
						scrollbarWidth: 'none',
						'&::-webkit-scrollbar': {
							display: 'none',
						},
					},
				}}>
				{/* IMPORTANT: pushes content below AppBar */}
				<Toolbar />

				{menu.map(section => (
					<Box key={section.label} sx={{ padding: '8px 0px 6px' }}>
						<Typography
							sx={{
								px: 2,
								pb: 1,
								fontSize: 11,
								color: 'rgba(255,255,255,0.4)',
								letterSpacing: 1,
							}}>
							{section.label}
						</Typography>

						<List dense sx={{ padding: 0 }}>
							{section.items.map(item => {
								return (
									<ListItemButton
										key={item.key}
										selected={tabName === item.key}
										onClick={() => {
											setTabName(item.key);
											navigate(item.key);
										}}
										sx={{
											borderRadius: 2,
											'&.Mui-selected': {
												bgcolor: '#00c4b426',
											},
											'&:hover': {
												bgcolor:
													'rgba(255,255,255,0.06)',
											},
										}}>
										<ListItemIcon sx={{ minWidth: 36 }}>
											<item.icon
												fontSize="small"
												sx={{
													color:
														tabName === item.key
															? teal
															: '#fff',
												}}
											/>
										</ListItemIcon>
										<ListItemText
											primary={item.label}
											primaryTypographyProps={{
												fontSize: '13px',
												fontWeight:
													tabName === item.key
														? '500'
														: '400',
												color:
													tabName === item.key
														? '#ffffff'
														: '#ffffff8c',
											}}
										/>
										{item.badge && (
											<Badge
												badgeContent={item.badge}
												sx={{
													'& .MuiBadge-badge': {
														bgcolor: teal,
														color: '#0f172a',
														marginRight: '10px',
													},
												}}
											/>
										)}
									</ListItemButton>
								);
							})}
						</List>

						<Divider
							sx={{ borderColor: 'rgba(255,255,255,0.05)' }}
						/>
					</Box>
				))}

				{/* FOOTER */}
				<Box sx={{ mt: 'auto', p: 2 }}>
					<Typography
						sx={{ fontSize: 12, color: 'rgba(255,255,255,0.7)' }}>
						{homeName}
					</Typography>

					<Stack
						direction="row"
						spacing={1}
						alignItems="center"
						sx={{ mt: 0.5 }}>
						<Box
							sx={{
								width: 7,
								height: 7,
								borderRadius: '50%',
								bgcolor: '#2d7a4a',
							}}
						/>
						<Typography
							sx={{
								fontSize: 11,
								color: 'rgba(255,255,255,0.35)',
							}}>
							All systems active
						</Typography>
					</Stack>
				</Box>
			</Drawer>
		</Box>
	);
}
